import React from 'react';
import { motion } from 'framer-motion';
import { BookOpen, Lightbulb } from 'lucide-react';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from 'recharts';
const mockData = [{
  week: 0,
  easi75: 0
}, {
  week: 2,
  easi75: 28
}, {
  week: 4,
  easi75: 45
}, {
  week: 8,
  easi75: 58
}, {
  week: 12,
  easi75: 65
}, {
  week: 16,
  easi75: 71
}];
const chapters = [{
  id: 1,
  title: 'Efficacy: EASI-75 Response',
  duration: '3:45'
}, {
  id: 2,
  title: 'Safety: Adverse Events Profile',
  duration: '4:10'
}, {
  id: 3,
  title: 'Long-term Data: 52-Week Results',
  duration: '3:30'
}, {
  id: 4,
  title: 'Quality of Life: DLQI Improvements',
  duration: '3:20'
}, {
  id: 5,
  title: 'Real-world Evidence: Registry Data',
  duration: '4:00'
}, {
  id: 6,
  title: 'Patient Selection: Ideal Candidates',
  duration: '3:50'
}];
export function LayerCore() {
  return <section className="py-24 bg-white" data-id="element-46">
      <div className="max-w-7xl mx-auto px-6" data-id="element-47">
        <motion.div initial={{
        opacity: 0,
        y: 20
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true
      }} transition={{
        duration: 0.6
      }} className="mb-16" data-id="element-48">
          <div className="flex items-center gap-3 mb-4" data-id="element-49">
            <div className="bg-red-600 text-white px-4 py-1 rounded-full text-sm font-semibold" data-id="element-50">
              LAYER 3
            </div>
            <BookOpen className="w-5 h-5 text-red-600" data-id="element-51" />
          </div>
          <h2 className="font-editorial text-4xl md:text-5xl font-bold text-slate-900 mb-4" data-id="element-52">
            The Scientific Core
          </h2>
          <p className="text-xl text-slate-600 max-w-3xl" data-id="element-53">
            Intentionally clean and vanilla — unbiased edutainment. KOLs walk
            through study graphs with practical presenting tips. This is where
            credibility lives.
          </p>
        </motion.div>

        <motion.div initial={{
        opacity: 0,
        y: 30
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true
      }} transition={{
        duration: 0.6
      }} className="bg-slate-50 rounded-2xl border-2 border-slate-200 overflow-hidden mb-8" data-id="element-54">
          {/* Mock Video Player */}
          <div className="relative bg-white p-8" data-id="element-55">
            <div className="flex items-start gap-6 mb-6" data-id="element-56">
              <div className="flex-1" data-id="element-57">
                <h3 className="text-2xl font-bold text-slate-900 mb-2" data-id="element-58">
                  EASI-75 Response Over 16 Weeks
                </h3>
                <p className="text-slate-600" data-id="element-59">ECZTRA 1 & 2 Pooled Analysis</p>
              </div>

              {/* KOL Corner Bubble */}
              <div className="w-32 h-32 rounded-full overflow-hidden border-4 border-slate-200 flex-shrink-0" data-id="element-60">
                <img src="https://images.unsplash.com/photo-1559839734-2b71ea197ec2?w=200&h=200&fit=crop" alt="Dr. Castellanos" className="w-full h-full object-cover" data-id="element-61" />
              </div>
            </div>

            {/* Chart */}
            <div className="h-80 mb-6" data-id="element-62">
              <ResponsiveContainer width="100%" height="100%" data-id="element-63">
                <LineChart data={mockData} data-id="element-64">
                  <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" data-id="element-65" />
                  <XAxis dataKey="week" label={{
                  value: 'Weeks',
                  position: 'insideBottom',
                  offset: -5
                }} stroke="#64748b" data-id="element-66" />
                  <YAxis label={{
                  value: 'EASI-75 Response (%)',
                  angle: -90,
                  position: 'insideLeft'
                }} stroke="#64748b" data-id="element-67" />
                  <Tooltip contentStyle={{
                  backgroundColor: '#1e293b',
                  border: 'none',
                  borderRadius: '8px',
                  color: '#fff'
                }} data-id="element-68" />
                  <Line type="monotone" dataKey="easi75" stroke="#E2231A" strokeWidth={3} dot={{
                  fill: '#E2231A',
                  r: 6
                }} data-id="element-69" />
                </LineChart>
              </ResponsiveContainer>
            </div>

            {/* Presenter Tip */}
            <div className="bg-amber-50 border-l-4 border-amber-500 p-4 rounded-r-lg" data-id="element-70">
              <div className="flex items-start gap-3" data-id="element-71">
                <Lightbulb className="w-5 h-5 text-amber-600 flex-shrink-0 mt-0.5" data-id="element-72" />
                <div data-id="element-73">
                  <div className="font-semibold text-amber-900 mb-1" data-id="element-74">
                    Presenter Tip
                  </div>
                  <div className="text-sm text-amber-800" data-id="element-75">
                    Pause here to let the audience absorb the 16-week response
                    curve. Emphasize the sustained improvement and clinical
                    significance of 71% EASI-75 response.
                  </div>
                </div>
              </div>
            </div>
          </div>
        </motion.div>

        {/* Chapter Timeline */}
        <motion.div initial={{
        opacity: 0,
        y: 20
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true
      }} transition={{
        duration: 0.6,
        delay: 0.2
      }} data-id="element-76">
          <h3 className="text-xl font-semibold text-slate-900 mb-4" data-id="element-77">
            All 6 Chapters
          </h3>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4" data-id="element-78">
            {chapters.map((chapter, index) => <div key={chapter.id} className="bg-slate-50 border border-slate-200 rounded-lg p-4 hover:border-red-300 transition-colors" data-id="element-79">
                <div className="flex items-center gap-3 mb-2" data-id="element-80">
                  <div className="w-8 h-8 rounded-full bg-red-600 text-white flex items-center justify-center text-sm font-semibold" data-id="element-81">
                    {chapter.id}
                  </div>
                  <div className="text-sm text-slate-500" data-id="element-82">
                    {chapter.duration}
                  </div>
                </div>
                <div className="font-medium text-slate-900" data-id="element-83">
                  {chapter.title}
                </div>
              </div>)}
          </div>
        </motion.div>
      </div>
    </section>;
}